import { combineReducers, configureStore } from "@reduxjs/toolkit";
import solarProductDetailsReducer from "./slices/solar-product-details-slice";
import firstLoginReducer from "./slices/first-login-slice";
import loaderReducer from "./slices/loader-slice";
import currentUserReducer from "./slices/current-user-slice";
import customerRegistrationReducer from "./slices/customer-registration-slice";
import solarRecommendationReducer from "./slices/solar-recommendation-slice";
import buySolarProductDetailsReducer from "./slices/buy-solar-product-slice";
import guestDelieveryAddressReducer from "./slices/guest-delivery-address-slice";
import signUpReducer from "./slices/sign-up-slice";
import { apiSlice } from "./base/api-slice";


export type RootState = ReturnType<typeof rootReducer>;

const rootReducer = combineReducers({
	[apiSlice.reducerPath]: apiSlice.reducer,
	user: currentUserReducer,
	solarProductDetails: solarProductDetailsReducer,
	firstLogin: firstLoginReducer,
	signUp: signUpReducer,
	loader: loaderReducer,
	customerRegistration: customerRegistrationReducer,
	solarRecommendation: solarRecommendationReducer,
	buySolarProductDetails: buySolarProductDetailsReducer,
	guestDeliveryAddress: guestDelieveryAddressReducer,
});

const loadStateFromSessionStorage = (): Partial<RootState> | undefined => {
	try {
		const serializedState = sessionStorage.getItem("reduxState");
		if (serializedState === null) return undefined; // No state found
		return { user: JSON.parse(serializedState) };
	} catch (err) {
		console.error("Could not load state from session storage:", err);
		return undefined;
	}
};

const saveStateToSessionStorage = (state: RootState) => {
	try {
		const serializedState = JSON.stringify(state.user);
		sessionStorage.setItem("reduxState", serializedState);
	} catch (err) {
		console.error("Could not save state to session storage:", err);
	}
};

const persistedState: Partial<RootState> | undefined = loadStateFromSessionStorage();

export const store = configureStore({
	reducer: rootReducer,
	preloadedState: persistedState,
	middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(apiSlice.middleware),
});
store.subscribe(() => {
	saveStateToSessionStorage(store.getState());
});

// Infer the `RootState` and `AppDispatch` types from the store itself

export type AppDispatch = typeof store.dispatch;
